import { useMemo } from 'react';
import { useLanguageStore } from '@/stores/language';
import { useSessionStore } from '@/stores/session';
import { createFormatters, type Formatters } from '@/utils/formatters';
import { sessionTimeZone } from '@/utils/organizationTimeZone';

/**
 * Форматтеры дат и чисел со связанной активной локалью.
 *
 * Нужен не ради краткости. Компонент, который форматирует дату или число, но
 * не вызывает t(), на смену языка не подписан — без обращения к стору он не
 * перерисуется и покажет дату в прежней локали до следующего обновления
 * данных. Чистые функции при этом остаются: их проверяют юнит-тесты сразу
 * на обеих локалях без рендеринга.
 *
 * Даты показываются в часовом поясе организации из сессии (контракты §5): хранение и
 * API — в UTC. Без пояса в сессии — UTC, как значение организации по умолчанию.
 * formatUtcDate — для границ, заданных сутками UTC (период обзора): в поясе организации
 * полночь UTC пришлась бы на другой день, и подпись назвала бы чужие сутки. formatDay — сутки
 * ряда по дням в поясе, которым сервер нарезал ряд (вызовы API по суткам организации); без
 * пояса ряда — в поясе организации.
 *
 * Форматировать дату мимо связанных форматтеров нельзя: чистые форматтеры принимают пояс необязательным
 * параметром, а забытый пояс означает пояс браузера. Это проверяет
 * tests/unit/timeZoneGuard.test.ts.
 */
/** Часовой пояс организации из сессии (IANA); пока он неизвестен — UTC */
export function useOrganizationTimeZone(): string {
	return useSessionStore(sessionTimeZone);
}

export function useFormatters(): Formatters {
	const locale = useLanguageStore((s) => s.language);
	const timeZone = useOrganizationTimeZone();

	return useMemo(() => createFormatters(locale, timeZone), [locale, timeZone]);
}
